import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from '@/hooks/use-toast';
import { updateAppointment } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { displayAge } from '@/lib/patientAge';
import { AppointmentDialogContent, DayPicker, Field, TimeSlots, dayLabel, timeLabel } from '@/components/Appointment/AppointmentFields';

interface Appointment {
  id: string;
  patient_id: string;
  doctor_id?: string;
  clinic_id?: string;
  patientName?: string;
  patientAge?: number;
  patientGender?: string;
  patientPhone?: string;
  date: string;
  time: string;
  reason: string;
  status: string;
}

interface EditAppointmentModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updatedAppointment: Appointment) => void;
}

const parseDateForInput = (dateStr: string): string => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

const STATUS_WARNINGS: Record<string, string> = {
  completed: 'This marks the appointment as completed.',
  cancelled: 'This cancels the appointment.',
  no_show: 'This marks the patient as a no-show.',
};

export default function EditAppointmentModal({
  appointment,
  isOpen,
  onClose,
  onUpdated
}: EditAppointmentModalProps) {
  const { currentUser } = useAuth();
  const isStaff = currentUser?.role === 'staff';

  const initial = useMemo(() => ({
    date: parseDateForInput(appointment?.date || ''),
    time: (appointment?.time || '').slice(0, 5),
    reason: appointment?.reason || '',
    status: appointment?.status || 'scheduled',
  }), [appointment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState(initial);
  const [isUpdating, setIsUpdating] = useState(false);

  // Re-populate the form when another appointment opens
  useEffect(() => { if (isOpen) setForm(initial); }, [isOpen, initial]);

  const day = form.date || format(new Date(), "yyyy-MM-dd");
  const isDirty = form.date !== initial.date || form.time !== initial.time || form.reason !== initial.reason || form.status !== initial.status;

  const statusWarning = form.status === initial.status ? null
    : form.status === 'scheduled' && initial.status === 'completed' ? 'This reverts a completed appointment to scheduled.'
    : STATUS_WARNINGS[form.status] ?? null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isDirty) { onClose(); return; }
    setIsUpdating(true);
    try {
      const updateData = {
        ...form,
        id: appointment.id,
        patient_id: appointment.patient_id,
        updated_at: new Date().toISOString()
      };
      await updateAppointment(updateData);
      onUpdated(updateData);
      toast({
        title: 'Appointment updated',
        description: [appointment.patientName, form.time ? `${dayLabel(form.date)}, ${timeLabel(form.time)}` : dayLabel(form.date)].filter(Boolean).join(' · '),
      });
      onClose();
    } catch {
      toast({ title: "Couldn't update the appointment", description: 'Try again in a moment.', variant: 'destructive' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <AppointmentDialogContent
        title="Edit appointment"
        onClose={onClose}
        footer={<>
          <Button type="button" variant="outline" onClick={onClose} disabled={isUpdating}>Cancel</Button>
          <Button type="submit" form="edit-appointment" disabled={isUpdating} className="gap-1.5">
            {isUpdating ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Save changes'}
          </Button>
        </>}
      >
        <form id="edit-appointment" onSubmit={onSubmit} className="space-y-4">
          {appointment?.patientName && (
            <Field label="Patient">
              <div className="min-h-9 rounded-md border border-border bg-muted/40 px-3 py-1.5">
                <p className="text-sm font-medium text-foreground capitalize truncate">{appointment.patientName}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[displayAge(appointment.patientAge), appointment.patientGender, appointment.patientPhone].filter(Boolean).join(' · ')}
                </p>
              </div>
            </Field>
          )}

          <Field label="Day">
            <DayPicker value={day} onChange={date => setForm(f => ({ ...f, date }))} disabled={isUpdating} />
          </Field>

          <Field label="Time">
            <TimeSlots
              day={day} value={form.time}
              onChange={time => setForm(f => ({ ...f, time }))}
              doctorId={appointment?.doctor_id ?? currentUser?.doctor_id ?? undefined}
              clinicId={appointment?.clinic_id ?? currentUser?.clinic_id ?? undefined}
              ignoreAppointmentId={appointment?.id}
              disabled={isUpdating}
            />
          </Field>

          {/* Status — doctor only */}
          {!isStaff && (
            <Field label="Status">
              <Select value={form.status} onValueChange={status => setForm(f => ({ ...f, status }))} disabled={isUpdating}>
                <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="no_show">No-show</SelectItem>
                </SelectContent>
              </Select>
              {statusWarning && (
                <p className="mt-2 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-foreground">
                  <AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5 flex-shrink-0" />
                  {statusWarning}
                </p>
              )}
            </Field>
          )}

          <Field label="Reason" htmlFor="edit-appointment-reason">
            <Input
              id="edit-appointment-reason" value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Fever for 3 days" disabled={isUpdating} className="h-9 text-sm"
            />
          </Field>
        </form>
      </AppointmentDialogContent>
    </Dialog>
  );
}
